/**
 * Budget Slider Range Calculation
 *
 * Single source of truth for the budget cap slider bounds, used by the
 * Team Calculator (and any future page with a budget control).
 *
 * Everyone starts the season at $100M (BUDGET_CAP). Each race, prices shift,
 * and transfers let a manager chase the risers. The max budget = $100M + the
 * sum, per round, of the 5 biggest driver rises + 2 biggest constructor rises;
 * the min mirrors it with the biggest drops (±$1M buffer). Nobody can land
 * outside that range, so clamping to it never blocks a real team.
 *
 * Resilience: when no meaningful range can be computed (missing price
 * history, early season, broken data), the range falls back to
 * BUDGET_RANGE_FALLBACK instead of collapsing to a useless ~$2M window.
 */

import {
  BUDGET_CAP,
  BUDGET_RANGE_FALLBACK,
  CONSTRUCTORS_PER_TEAM,
  DRIVERS_PER_TEAM,
  MIN_BUDGET_RANGE_WIDTH,
} from "./constants";

/**
 * Whether a $price pick fits the budget given current spending.
 *
 * Prices and the budget are all 1-decimal values, so the raw subtraction
 * drifts (e.g. 109.6 - 84.5 = 25.0999…94) and would wrongly reject a pick that
 * costs exactly the remaining budget — like a $25.1M constructor that completes
 * a $109.6M team. Rounding the remaining to whole tenths recovers the true
 * value, so exact-budget picks are affordable while genuinely over-budget picks
 * (e.g. $25.2M) are still rejected.
 */
export function canAffordPick(
  price: number,
  budget: number,
  currentSpending: number,
): boolean {
  const remaining = Math.round((budget - currentSpending) * 10) / 10;
  return price <= remaining;
}

/** An asset's price going into one round of the season. */
export interface SeasonPriceRow {
  slug: string;
  round: number;
  price: number;
}

export interface BudgetRange {
  budgetMin: number;
  budgetMax: number;
}

/**
 * Sum, over every round-to-round step, of the `k` biggest rises (`up`) and the
 * `k` biggest drops (`down`). Transfers let a manager hold different assets
 * each round, so this — not "held the same team since round 1" — is the true
 * bound on how far a budget can have moved.
 */
function roundSwings(
  rows: SeasonPriceRow[],
  k: number,
): { up: number; down: number; assets: number } {
  const byAsset = new Map<string, Map<number, number>>();
  for (const { slug, round, price } of rows) {
    if (!byAsset.has(slug)) byAsset.set(slug, new Map());
    byAsset.get(slug)?.set(round, price);
  }
  const rounds = [...new Set(rows.map((r) => r.round))].sort((a, b) => a - b);

  let up = 0;
  let down = 0;
  for (let i = 1; i < rounds.length; i++) {
    const deltas: number[] = [];
    for (const prices of byAsset.values()) {
      const prev = prices.get(rounds[i - 1]);
      const next = prices.get(rounds[i]);
      if (prev !== undefined && next !== undefined) deltas.push(next - prev);
    }
    deltas.sort((a, b) => a - b);
    up += deltas
      .slice(-k)
      .filter((d) => d > 0)
      .reduce((s, d) => s + d, 0);
    down += deltas
      .slice(0, k)
      .filter((d) => d < 0)
      .reduce((s, d) => s + d, 0);
  }
  return { up, down, assets: byAsset.size };
}

/**
 * Budget slider bounds from the FULL season's price rows (every round up to
 * the selected race). Passing only a recent window silently drops early-season
 * gains and caps real budgets (the "$122.8M max at Italy" bug).
 */
export function computeBudgetRange(
  driverPrices: SeasonPriceRow[],
  constructorPrices: SeasonPriceRow[],
): BudgetRange {
  const d = roundSwings(driverPrices, DRIVERS_PER_TEAM);
  const c = roundSwings(constructorPrices, CONSTRUCTORS_PER_TEAM);

  if (d.assets < DRIVERS_PER_TEAM || c.assets < CONSTRUCTORS_PER_TEAM) {
    return {
      budgetMin: BUDGET_RANGE_FALLBACK.min,
      budgetMax: BUDGET_RANGE_FALLBACK.max,
    };
  }

  // Prices are 1-decimal; round away float drift before floor/ceil below.
  const maxGain = Math.round((d.up + c.up) * 10) / 10;
  const minGain = Math.round((d.down + c.down) * 10) / 10;

  const budgetMin = Math.floor((BUDGET_CAP + minGain - 1) * 10) / 10;
  const budgetMax = Math.ceil((BUDGET_CAP + maxGain + 1) * 10) / 10;

  // Degenerate range guard: with no season price history all gains are 0 and
  // the range collapses to BUDGET_CAP ± 1 (the "99–101" bug). A slider that
  // narrow is useless — fall back to safe defaults instead.
  if (budgetMax - budgetMin < MIN_BUDGET_RANGE_WIDTH) {
    return {
      budgetMin: BUDGET_RANGE_FALLBACK.min,
      budgetMax: BUDGET_RANGE_FALLBACK.max,
    };
  }

  return { budgetMin, budgetMax };
}
